import React, { useState } from 'react'
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  InputAdornment,
} from '@mui/material'
import { Search, Radio, Mic, ChevronRight, Check, Sparkles } from 'lucide-react'
import AnimatedLoadingText from 'components/AnimatedLoadingText/AnimatedLoadingText'
import { LOCAL, DEVELOP } from 'constants/env'
import { Platform } from 'graphql/generated/graphql'

const config = process.env.NODE_ENV === 'development' ? LOCAL : DEVELOP

interface PodcastResult {
  id: number
  title: string
  description: string
  author: string
  image: string
  categories: Record<string, string>
}

const PLATFORM_OPTIONS: Array<{ value: Platform; label: string; emoji: string }> = [
  { value: Platform.Instagram, label: 'Instagram', emoji: '📷' },
  { value: Platform.TikTok, label: 'TikTok', emoji: '🎵' },
  { value: Platform.Youtube, label: 'YouTube', emoji: '🎥' },
  { value: Platform.Reddit, label: 'Reddit', emoji: '🔴' },
  { value: Platform.Twitter, label: 'Twitter', emoji: '🐦' },
]

type Step = 'search' | 'select' | 'platforms' | 'creating'

interface PodcastOnboardingProps {
  onComplete: () => void
  onSkip: () => void
}

export default function PodcastOnboarding({ onComplete, onSkip }: PodcastOnboardingProps): JSX.Element {
  const [step, setStep] = useState<Step>('search')
  const [searchTerm, setSearchTerm] = useState('')
  const [results, setResults] = useState<PodcastResult[]>([])
  const [searching, setSearching] = useState(false)
  const [selectedPodcast, setSelectedPodcast] = useState<PodcastResult | null>(null)
  const [selectedPlatforms, setSelectedPlatforms] = useState<Platform[]>([Platform.Instagram, Platform.TikTok])
  const [creating, setCreating] = useState(false)
  const [createdCount, setCreatedCount] = useState(0)
  const [error, setError] = useState<string | null>(null)

  const handleSearch = async (): Promise<void> => {
    if (!searchTerm.trim()) return
    setSearching(true)
    setError(null)
    try {
      const response = await fetch(
        `${config.API_URL}/api/podcast/search?q=${encodeURIComponent(searchTerm)}`,
        { credentials: 'include' }
      )
      const data = await response.json()
      if (data.success) {
        setResults(data.data ?? [])
        setStep('select')
      } else {
        setError(data.error ?? 'Search failed. Please try again.')
      }
    } catch (err) {
      console.error('Podcast search error:', err)
      setError('Unable to search. Please check your connection and try again.')
    } finally {
      setSearching(false)
    }
  }

  const handleSelectPodcast = (podcast: PodcastResult): void => {
    setSelectedPodcast(podcast)
    setStep('platforms')
  }

  const handleCreate = async (): Promise<void> => {
    if (!selectedPodcast) return
    setStep('creating')
    setCreating(true)
    setError(null)
    try {
      const response = await fetch(`${config.API_URL}/api/podcast/onboard`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          feedId: selectedPodcast.id,
          platforms: selectedPlatforms,
        }),
      })
      const data = await response.json()
      if (data.success) {
        setCreatedCount(data.data.topicsCreated)
        setTimeout(() => onComplete(), 1500)
      } else {
        setError(data.error ?? 'Failed to set up your podcast. Please try again.')
        setStep('platforms')
      }
    } catch (err) {
      console.error('Podcast onboard error:', err)
      setError('Something went wrong. Please try again.')
      setStep('platforms')
    } finally {
      setCreating(false)
    }
  }

  const togglePlatform = (p: Platform): void => {
    setSelectedPlatforms((prev) =>
      prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]
    )
  }

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', py: 4 }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Box sx={{ width: 64, height: 64, borderRadius: '16px', background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
          <Mic size={28} color="white" />
        </Box>
        <Typography sx={{ color: 'white', fontWeight: 700, fontSize: { xs: '22px', md: '28px' }, mb: 1 }}>
          {step === 'search' ? 'Find your podcast' :
           step === 'select' ? 'Is this your show?' :
           step === 'platforms' ? 'Where do you want to grow?' :
           'Setting up your Social Radar...'}
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '15px' }}>
          {step === 'search' ? "We'll auto-create engagement signals based on your podcast topics." :
           step === 'select' ? 'Select your podcast from the results below.' :
           step === 'platforms' ? 'Choose the platforms where you want to find conversations.' :
           `Creating ${createdCount > 0 ? createdCount : ''} signals from your podcast data...`}
        </Typography>
        {error && (
          <Box sx={{ mt: 1.5, p: 1.5, backgroundColor: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '10px' }}>
            <Typography sx={{ color: '#ef4444', fontSize: '13px', textAlign: 'center' }}>
              {error}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Search step */}
      {step === 'search' && (
        <Box>
          <Box sx={{ display: 'flex', gap: 1.5, mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Search for your podcast name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { handleSearch().catch(console.error) } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} color="rgba(255,255,255,0.4)" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  borderRadius: '12px',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                  '&:hover fieldset': { borderColor: 'rgba(168,85,247,0.4)' },
                  '&.Mui-focused fieldset': { borderColor: '#a855f7' },
                },
              }}
            />
            <Button
              onClick={() => { handleSearch().catch(console.error) }}
              disabled={!searchTerm.trim() || searching}
              sx={{
                background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)',
                color: 'white',
                borderRadius: '12px',
                textTransform: 'none',
                px: 3,
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              {searching ? <><CircularProgress size={16} sx={{ color: 'white', mr: 1 }} /> Searching</> : 'Search'}
            </Button>
          </Box>
          <Button
            onClick={onSkip}
            sx={{ color: 'rgba(255,255,255,0.4)', textTransform: 'none', fontSize: '13px', display: 'block', mx: 'auto' }}
          >
            Skip — I&apos;ll set up manually
          </Button>
        </Box>
      )}

      {/* Select step */}
      {step === 'select' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {results.length === 0 && (
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', py: 4 }}>
              No podcasts found. Try a different search term.
            </Typography>
          )}
          {results.map((podcast) => (
            <Box
              key={podcast.id}
              onClick={() => handleSelectPodcast(podcast)}
              sx={{
                display: 'flex',
                gap: 2,
                p: 2,
                backgroundColor: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '14px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  backgroundColor: 'rgba(168,85,247,0.08)',
                  borderColor: 'rgba(168,85,247,0.3)',
                },
              }}
            >
              {podcast.image ? (
                <Box
                  component="img"
                  src={podcast.image}
                  sx={{ width: 56, height: 56, borderRadius: '10px', objectFit: 'cover', flexShrink: 0 }}
                />
              ) : (
                <Box sx={{ width: 56, height: 56, borderRadius: '10px', backgroundColor: 'rgba(168,85,247,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Radio size={24} color="#a855f7" />
                </Box>
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '14px', mb: 0.25 }}>
                  {podcast.title}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', mb: 0.5 }}>
                  by {podcast.author}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  {podcast.description.replace(/<[^>]*>/g, '')}
                </Typography>
              </Box>
              <ChevronRight size={20} color="rgba(255,255,255,0.3)" style={{ alignSelf: 'center', flexShrink: 0 }} />
            </Box>
          ))}
          <Button
            onClick={() => { setStep('search'); setResults([]) }}
            sx={{ color: 'rgba(255,255,255,0.4)', textTransform: 'none', fontSize: '13px', mt: 1 }}
          >
            Search again
          </Button>
        </Box>
      )}

      {/* Platform selection step */}
      {step === 'platforms' && selectedPodcast && (
        <Box>
          {/* Selected podcast preview */}
          <Box sx={{ display: 'flex', gap: 2, p: 2, mb: 3, backgroundColor: 'rgba(168,85,247,0.08)', border: '1px solid rgba(168,85,247,0.2)', borderRadius: '14px' }}>
            {selectedPodcast.image && (
              <Box component="img" src={selectedPodcast.image} sx={{ width: 48, height: 48, borderRadius: '10px', objectFit: 'cover' }} />
            )}
            <Box>
              <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '14px' }}>{selectedPodcast.title}</Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>by {selectedPodcast.author}</Typography>
            </Box>
          </Box>

          <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', mb: 1.5 }}>
            Select the platforms where you want to find and engage with your audience:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
            {PLATFORM_OPTIONS.map((p) => (
              <Button
                key={p.value}
                onClick={() => togglePlatform(p.value)}
                sx={{
                  backgroundColor: selectedPlatforms.includes(p.value) ? 'rgba(168,85,247,0.25)' : 'rgba(255,255,255,0.05)',
                  border: selectedPlatforms.includes(p.value) ? '1px solid rgba(168,85,247,0.4)' : '1px solid rgba(255,255,255,0.1)',
                  color: 'white',
                  borderRadius: '10px',
                  textTransform: 'none',
                  px: 2,
                  py: 1,
                  '&:hover': { backgroundColor: 'rgba(168,85,247,0.15)' },
                }}
              >
                {p.emoji} {p.label}
              </Button>
            ))}
          </Box>

          <Button
            onClick={() => { handleCreate().catch(console.error) }}
            disabled={selectedPlatforms.length === 0}
            startIcon={<Sparkles size={18} />}
            fullWidth
            sx={{
              background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)',
              color: 'white',
              borderRadius: '12px',
              textTransform: 'none',
              py: 1.5,
              fontWeight: 600,
              fontSize: '15px',
            }}
          >
            Set Up My Social Radar
          </Button>
        </Box>
      )}

      {/* Creating step */}
      {step === 'creating' && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          {creating ? (
            <>
              <CircularProgress sx={{ color: '#a855f7', mb: 3 }} size={40} />
              <AnimatedLoadingText
                messages={[
                  'Analyzing your podcast topics',
                  'Generating search keywords',
                  'Setting up your Social Radar signals',
                  'Scanning for conversations',
                ]}
                interval={2500}
              />
            </>
          ) : (
            <>
              <Box sx={{ width: 48, height: 48, borderRadius: '50%', backgroundColor: 'rgba(34,197,94,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
                <Check size={24} color="#22c55e" />
              </Box>
              <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '18px', mb: 0.5 }}>
                {createdCount} signals created
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px' }}>
                Your Social Radar is scanning for conversations now...
              </Typography>
            </>
          )}
        </Box>
      )}
    </Box>
  )
}
